import { fetchAuthSession } from 'aws-amplify/auth';
import API_CONFIG from '@/lib/api-config';

// Analytics utility for tracking user events
type EventProperties = Record<string, unknown>;

interface AnalyticsEvent {
    event: string;
    properties?: EventProperties;
    timestamp?: number;
}

const ANONYMOUS_EVENTS = new Set([
    'landing_engagement',
    'landing_conversion',
    'landing_section_view',
    'tool_started',
    'tool_completed',
    'tool_selected',
    'result_to_signup',
    'share_opened',
    'share_generated',
    'share_completed',
    'pwa_install_prompted',
    'pwa_installed',
]);

class Analytics {
    private static instance: Analytics;
    private queue: AnalyticsEvent[] = [];
    private isEnabled: boolean = true;

    private constructor() {
        this.isEnabled = typeof window !== 'undefined';
    }

    static getInstance(): Analytics {
        if (!Analytics.instance) {
            Analytics.instance = new Analytics();
        }
        return Analytics.instance;
    }

    // Track any event
    track(event: string, properties?: EventProperties) {
        if (!this.isEnabled) return;

        const analyticsEvent: AnalyticsEvent = {
            event,
            properties: {
                ...properties,
                timestamp: Date.now(),
                path: window.location.pathname,
            },
        };

        this.queue.push(analyticsEvent);
        void this.flush();

        const analyticsWindow = window as Window & { gtag?: (...args: unknown[]) => void };
        analyticsWindow.gtag?.('event', event, properties || {});

        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
            console.info('Analytics:', event, properties);
        }
    }

    // Profile completion tracking
    trackProfileCompletion(percentage: number, completedFields: string[]) {
        this.track('profile_completion_progress', {
            percentage,
            completed_fields: completedFields,
            milestone: this.getMilestone(percentage),
        });
    }

    // Profile milestone tracking
    trackProfileMilestone(milestone: 25 | 50 | 75 | 100) {
        this.track('profile_milestone_reached', {
            milestone,
            badge_earned: this.getBadgeName(milestone),
        });
    }

    // Feature usage tracking
    trackFeatureUsed(feature: string, metadata?: EventProperties) {
        this.track('feature_used', {
            feature,
            ...metadata,
        });
    }

    // Onboarding tracking
    trackOnboardingStep(step: string, completed: boolean) {
        this.track('onboarding_step', {
            step,
            completed,
        });
    }

    trackOnboardingComplete() {
        this.track('onboarding_completed', {
            completion_date: new Date().toISOString(),
        });
    }

    // Payment tracking
    trackPaymentSuccess(plan: string, amount: number) {
        this.track('payment_success', {
            plan,
            amount,
        });
    }

    // Helper methods
    private getMilestone(percentage: number): number | null {
        const milestones = [25, 50, 75, 100];
        return milestones.find(m => percentage >= m && percentage < m + 25) || null;
    }

    private getBadgeName(milestone: number): string {
        const badges: Record<number, string> = {
            25: 'Profile Starter',
            50: 'Profile Builder',
            75: 'Profile Master',
            100: 'Cosmic Profile',
        };
        return badges[milestone] || '';
    }

    // Flush authenticated events to the first-party analytics store.
    private async flush() {
        if (this.queue.length === 0) return;

        const events = [...this.queue];
        this.queue = [];

        if (typeof window !== 'undefined') {
            try {
                const existing = JSON.parse(localStorage.getItem('analytics_events') || '[]');
                localStorage.setItem(
                    'analytics_events',
                    JSON.stringify([...existing.slice(-100), ...events])
                );
            } catch (e) {
                console.error('Failed to store analytics:', e);
            }
        }

        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            const deliverableEvents = token
                ? events
                : events.filter(({ event }) => ANONYMOUS_EVENTS.has(event));
            if (deliverableEvents.length === 0) return;
            const endpoint = token ? API_CONFIG.user.analytics() : API_CONFIG.analytics.anonymous();

            const responses = await Promise.all(
                deliverableEvents.map(({ event, properties }) =>
                    fetch(endpoint, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        },
                        body: JSON.stringify({ event, properties }),
                    })
                )
            );
            if (process.env.NODE_ENV === 'development' && responses.some((response) => !response.ok)) {
                console.warn('Some analytics events were rejected by the API.');
            }
        } catch (error) {
            // Anonymous and offline events remain available in the local debug queue.
            if (process.env.NODE_ENV === 'development') {
                console.warn('Analytics delivery failed.', error);
            }
        }
    }
}

export const analytics = Analytics.getInstance();

// Convenience exports
export const trackProfileCompletion = (percentage: number, completedFields: string[]) =>
    analytics.trackProfileCompletion(percentage, completedFields);

export const trackProfileMilestone = (milestone: 25 | 50 | 75 | 100) =>
    analytics.trackProfileMilestone(milestone);

export const trackFeatureUsed = (feature: string, metadata?: EventProperties) =>
    analytics.trackFeatureUsed(feature, metadata);

export const trackOnboardingStep = (step: string, completed: boolean) =>
    analytics.trackOnboardingStep(step, completed);

export const trackOnboardingComplete = () =>
    analytics.trackOnboardingComplete();

export const trackPaymentSuccess = (plan: string, amount: number) =>
    analytics.trackPaymentSuccess(plan, amount);

export const trackGrowthEvent = (event: string, properties?: EventProperties) =>
    analytics.track(event, properties);

export default analytics;
